import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import PortfolioPieceRenderer from "@/components/PortfolioPieceRenderer";

// Set noindex meta on public-portfolio pages so generated per-application
// pages aren't crawled by search engines.
function useNoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);
}

export default function PublicPortfolio() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useNoIndexMeta();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/portfolio/${slug}`);
        setData(res.data);
      } catch {
        setError("Portfolio not found");
      }
    })();
  }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-xs uppercase tracking-[0.3em]">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div>;

  const { candidate, job, portfolio_intro, pieces } = data;

  return (
    <div className="min-h-screen pb-24">
      <header className="border-b border-foreground/30 glass-strong sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <Logo size="sm" />
          <div className="flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground hidden sm:block">PORTFOLIO / SHARED</div>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <section className="border-b border-foreground/30 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-12 sm:py-20 md:py-32 relative">
          <div className="terminal-label mb-5 sm:mb-6" data-testid="portfolio-context-label">
            Prepared for / {job?.company || "Viewer"}
          </div>
          <div className="flex items-start gap-5 sm:gap-8 flex-wrap">
            {candidate.headshot_path && (
              <div className="w-24 h-24 sm:w-36 sm:h-36 border border-foreground/40 overflow-hidden flex-shrink-0" data-testid="portfolio-headshot">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${candidate.headshot_path}?auth=public`}
                  alt={candidate.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.parentElement.style.display = "none"; }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="serif-mod text-[2.5rem] leading-[1] sm:text-7xl sm:leading-[0.95] lg:text-[9rem] lg:leading-[0.92] tracking-[-0.04em] max-w-5xl break-words" style={{ fontWeight: 600 }}>
                {candidate.name}
              </h1>
              <div className="mt-4 sm:mt-6 text-sm sm:text-base text-foreground/70">
                {candidate.title}{candidate.location ? ` · ${candidate.location}` : ""}
              </div>
              {(candidate.linkedin_url || candidate.behance_url) && (
                <div className="mt-4 flex gap-2 sm:gap-3 flex-wrap">
                  {candidate.linkedin_url && (
                    <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-[10px] inline-flex items-center gap-2" data-testid="portfolio-linkedin">
                      LinkedIn ↗
                    </a>
                  )}
                  {candidate.behance_url && (
                    <a href={candidate.behance_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-[10px] inline-flex items-center gap-2" data-testid="portfolio-behance">
                      Behance ↗
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
          {portfolio_intro && (
            <p className="mt-8 sm:mt-12 max-w-3xl text-base sm:text-lg md:text-xl leading-relaxed text-foreground/85 serif-mod" style={{ fontWeight: 300 }}>{portfolio_intro}</p>
          )}
          {job && (
            <div className="mt-8 sm:mt-10 inline-block panel-glass px-3 sm:px-4 py-2 max-w-full">
              <span className="terminal-label">For role: </span>
              <span className="text-xs sm:text-sm break-words" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{job.title} @ {job.company}</span>
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-foreground/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-10 sm:py-16">
          <div className="terminal-label mb-6 sm:mb-8">Selected work</div>
          {pieces.length === 0 ? (
            <div className="text-sm text-foreground/60">No pieces selected for this role.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pieces.map((p, i) => (
                <PortfolioPieceRenderer key={p.id || i} piece={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {candidate.bio && (
        <section className="border-b border-foreground/30">
          <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-10 sm:py-16">
            <div className="terminal-label mb-5 sm:mb-6">About</div>
            <p className="serif-mod text-xl sm:text-2xl md:text-3xl max-w-3xl text-foreground/90 leading-snug" style={{ fontWeight: 300 }}>{candidate.bio}</p>
          </div>
        </section>
      )}

      <footer className="py-8">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 flex justify-between items-center gap-3">
          <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Made with Sisyphean</span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground blink-caret">END</span>
        </div>
      </footer>
    </div>
  );
}
